<!-- Backdrop -->
<div
  class="fixed inset-0 z-40 bg-black/30 transition-opacity duration-200"
  [class.opacity-0]="!open"
  [class.pointer-events-none]="!open"
  (click)="requestClose()"
  aria-hidden="true"
></div>

<!-- Drawer panel -->
<div
  class="fixed bottom-0 right-0 top-0 z-50 flex w-[480px] max-w-full flex-col bg-white shadow-2xl transition-transform duration-300 ease-in-out"
  [class.translate-x-full]="!open"
  role="dialog"
  [attr.aria-label]="'Settings for ' + (itemId ?? 'item')"
  [attr.aria-modal]="open"
>

  <!-- ── Header ─────────────────────────────────────────────────────────────── -->
  <div class="flex flex-shrink-0 items-center justify-between border-b border-slate-200 px-5 py-4">
    <div class="flex min-w-0 items-center gap-3">
      <div class="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-slate-100">
        <lucide-icon [name]="service?.icon ?? 'settings'" [size]="15" class="text-slate-600" [strokeWidth]="1.75"></lucide-icon>
      </div>
      <div class="min-w-0">
        <div class="flex items-center gap-1 text-[11px] text-slate-400">
          <span class="font-medium text-slate-500">{{ service?.name }}</span>
          <lucide-icon name="chevron-right" [size]="10" [strokeWidth]="2"></lucide-icon>
          <span>Entry Configuration</span>
        </div>
        <h2 class="truncate font-mono text-sm font-semibold text-slate-900" [title]="itemId ?? ''">
          {{ itemId }}
        </h2>
      </div>
    </div>
    <button
      type="button"
      (click)="requestClose()"
      class="ml-3 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-violet-300"
      aria-label="Close"
    >
      <lucide-icon name="x" [size]="15" [strokeWidth]="2"></lucide-icon>
    </button>
  </div>

  <!-- ── Discard-changes confirmation banner ────────────────────────────────── -->
  <div
    *ngIf="showDiscardConfirm"
    class="flex-shrink-0 border-b border-amber-200 bg-amber-50 px-5 py-4"
    role="alert"
  >
    <p class="text-sm font-semibold text-amber-900">Discard unsaved changes?</p>
    <p class="mt-0.5 text-xs text-amber-700">Your edits will be lost if you close without saving.</p>
    <div class="mt-3 flex gap-2">
      <button type="button" (click)="confirmDiscard()"
        class="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-amber-400"
      >Discard changes</button>
      <button type="button" (click)="cancelDiscard()"
        class="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300"
      >Keep editing</button>
    </div>
  </div>

  <!-- ── Body ───────────────────────────────────────────────────────────────── -->
  <div class="flex-1 overflow-y-auto" *ngIf="draft">

    <!-- ── Monitoring toggle ─────────────────────────────────────────────────── -->
    <section class="border-b border-slate-100 px-5 py-4">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <lucide-icon name="activity" [size]="13" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
          <div>
            <p class="text-xs font-semibold text-slate-700">Monitoring</p>
            <p class="text-[11px] text-slate-400" *ngIf="quotaInfo">
              {{ quotaInfo.currentCount }} / {{ quotaInfo.limit }} monitored
              <span *ngIf="quotaInfo.remaining === 0 && !isMonitored" class="ml-1 font-medium text-amber-600">— limit reached</span>
            </p>
          </div>
        </div>
        <button
          type="button"
          (click)="toggleMonitoring()"
          [disabled]="monitoringState === 'saving'"
          class="relative flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:ring-offset-2 disabled:opacity-50"
          [class.bg-violet-600]="isMonitored"
          [class.bg-slate-200]="!isMonitored"
          role="switch"
          [attr.aria-checked]="isMonitored"
          [attr.aria-label]="'Toggle monitoring for ' + (itemId ?? 'this item')"
        >
          <span
            class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200"
            [class.translate-x-5]="isMonitored"
            [class.translate-x-0]="!isMonitored"
          ></span>
        </button>
      </div>
    </section>

    <!-- ── Override notice bar ────────────────────────────────────────────────── -->
    <div *ngIf="hasAnyOverride" class="border-b border-violet-100 bg-violet-50 px-5 py-2.5">
      <div class="flex items-center justify-between gap-3">
        <div class="flex items-center gap-1.5 text-xs text-violet-700">
          <lucide-icon name="settings" [size]="11" [strokeWidth]="2"></lucide-icon>
          <span>This entry has custom overrides — some channels differ from the service default.</span>
        </div>
        <button type="button" (click)="revertToService()"
          class="flex-shrink-0 text-[11px] font-medium text-violet-600 hover:text-violet-800 focus:outline-none"
        >Reset all to service →</button>
      </div>
    </div>

    <!-- ── Notification Channels ──────────────────────────────────────────────── -->
    <section class="px-5 py-5">
      <div class="mb-4 flex items-center gap-2">
        <lucide-icon name="bell" [size]="13" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500">Notification Channels</h3>
      </div>

      <!-- ── Slack ──────────────────────────────────────────────────────────── -->
      <div class="mb-3 overflow-hidden rounded-lg border bg-white"
        [class.border-slate-200]="draft.notifications.slack.mode === 'service'"
        [class.border-violet-200]="draft.notifications.slack.mode !== 'service'"
      >
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <lucide-icon name="message-square" [size]="14" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
            <span class="text-sm font-medium text-slate-900">Slack</span>
            <span *ngIf="draft.notifications.slack.mode !== 'service'"
              class="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Custom</span>
          </div>
          <select
            [ngModel]="draft.notifications.slack.mode"
            (ngModelChange)="setNotifMode('slack', $event)"
            [ngModelOptions]="{standalone: true}"
            class="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-300"
          >
            <option value="service">Use service setting</option>
            <option value="disabled">Disabled for this entry</option>
            <option value="enabled-inherited">Enabled — use service destination</option>
            <option value="enabled-custom">Enabled — use custom destination</option>
          </select>
        </div>
        <!-- Service mode: show effective summary -->
        <div *ngIf="draft.notifications.slack.mode === 'service'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">{{ slackEffectiveSummary() }}</p>
        </div>
        <!-- Disabled mode -->
        <div *ngIf="draft.notifications.slack.mode === 'disabled'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">Slack disabled for this entry.</p>
        </div>
        <!-- Enabled-inherited: show resolved destination -->
        <div *ngIf="draft.notifications.slack.mode === 'enabled-inherited'" class="border-t border-slate-100 px-4 py-2.5 space-y-1.5">
          <div *ngIf="!slackInheritedMissing" class="flex items-center gap-1.5 text-xs text-slate-500">
            <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
            <span>{{ slackEffectiveSummary() }}</span>
          </div>
          <div *ngIf="slackInheritedMissing" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <span class="font-medium">No destination available.</span> The service has no Slack destination configured. Use a custom destination or configure the service first.
          </div>
        </div>
        <!-- Enabled-custom: URL input -->
        <div *ngIf="draft.notifications.slack.mode === 'enabled-custom'" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-2">
          <label class="block text-xs font-medium text-slate-700">Webhook URL <span class="text-red-500">*</span></label>
          <input
            type="url"
            [(ngModel)]="draft.notifications.slack.overrideConfig!.webhookUrl"
            [ngModelOptions]="{standalone: true}"
            placeholder="https://hooks.slack.com/services/…"
            class="w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1"
            [class.border-slate-200]="!slackUrlError" [class.focus:border-violet-400]="!slackUrlError" [class.focus:ring-violet-300]="!slackUrlError"
            [class.border-red-300]="slackUrlError"   [class.focus:ring-red-300]="slackUrlError"
            (ngModelChange)="draft.notifications.slack.overrideConfig = { webhookUrl: $event }"
          >
          <p *ngIf="slackUrlError" class="text-xs text-red-500">Enter a valid https:// URL.</p>
        </div>
      </div>

      <!-- ── Microsoft Teams ────────────────────────────────────────────────── -->
      <div class="mb-3 overflow-hidden rounded-lg border bg-white"
        [class.border-slate-200]="draft.notifications.teams.mode === 'service'"
        [class.border-violet-200]="draft.notifications.teams.mode !== 'service'"
      >
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <lucide-icon name="users" [size]="14" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
            <span class="text-sm font-medium text-slate-900">Microsoft Teams</span>
            <span *ngIf="draft.notifications.teams.mode !== 'service'"
              class="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Custom</span>
          </div>
          <select
            [ngModel]="draft.notifications.teams.mode"
            (ngModelChange)="setNotifMode('teams', $event)"
            [ngModelOptions]="{standalone: true}"
            class="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-300"
          >
            <option value="service">Use service setting</option>
            <option value="disabled">Disabled for this entry</option>
            <option value="enabled-inherited">Enabled — use service destination</option>
            <option value="enabled-custom">Enabled — use custom destination</option>
          </select>
        </div>
        <div *ngIf="draft.notifications.teams.mode === 'service'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">{{ teamsEffectiveSummary() }}</p>
        </div>
        <div *ngIf="draft.notifications.teams.mode === 'disabled'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">Teams disabled for this entry.</p>
        </div>
        <div *ngIf="draft.notifications.teams.mode === 'enabled-inherited'" class="border-t border-slate-100 px-4 py-2.5 space-y-1.5">
          <div *ngIf="!teamsInheritedMissing" class="flex items-center gap-1.5 text-xs text-slate-500">
            <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
            <span>{{ teamsEffectiveSummary() }}</span>
          </div>
          <div *ngIf="teamsInheritedMissing" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <span class="font-medium">No destination available.</span> The service has no Teams destination configured.
          </div>
        </div>
        <div *ngIf="draft.notifications.teams.mode === 'enabled-custom'" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-2">
          <label class="block text-xs font-medium text-slate-700">Webhook URL <span class="text-red-500">*</span></label>
          <input
            type="url"
            [(ngModel)]="draft.notifications.teams.overrideConfig!.webhookUrl"
            [ngModelOptions]="{standalone: true}"
            placeholder="https://outlook.office.com/webhook/…"
            class="w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1"
            [class.border-slate-200]="!teamsUrlError" [class.focus:border-violet-400]="!teamsUrlError" [class.focus:ring-violet-300]="!teamsUrlError"
            [class.border-red-300]="teamsUrlError"   [class.focus:ring-red-300]="teamsUrlError"
            (ngModelChange)="draft.notifications.teams.overrideConfig = { webhookUrl: $event }"
          >
          <p *ngIf="teamsUrlError" class="text-xs text-red-500">Enter a valid https:// URL.</p>
        </div>
      </div>

      <!-- ── Email ──────────────────────────────────────────────────────────── -->
      <div class="overflow-hidden rounded-lg border bg-white"
        [class.border-slate-200]="draft.notifications.email.mode === 'service'"
        [class.border-violet-200]="draft.notifications.email.mode !== 'service'"
      >
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <lucide-icon name="mail" [size]="14" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
            <span class="text-sm font-medium text-slate-900">Email</span>
            <span *ngIf="draft.notifications.email.mode !== 'service'"
              class="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Custom</span>
          </div>
          <select
            [ngModel]="draft.notifications.email.mode"
            (ngModelChange)="setNotifMode('email', $event)"
            [ngModelOptions]="{standalone: true}"
            class="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-300"
          >
            <option value="service">Use service setting</option>
            <option value="disabled">Disabled for this entry</option>
            <option value="enabled-inherited">Enabled — use service destination</option>
            <option value="enabled-custom">Enabled — use custom destination</option>
          </select>
        </div>
        <div *ngIf="draft.notifications.email.mode === 'service'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">{{ emailEffectiveSummary() }}</p>
        </div>
        <div *ngIf="draft.notifications.email.mode === 'disabled'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">Email disabled for this entry.</p>
        </div>
        <div *ngIf="draft.notifications.email.mode === 'enabled-inherited'" class="border-t border-slate-100 px-4 py-2.5 space-y-1.5">
          <div *ngIf="!emailInheritedMissing" class="flex items-center gap-1.5 text-xs text-slate-500">
            <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
            <span>{{ emailEffectiveSummary() }}</span>
          </div>
          <div *ngIf="emailInheritedMissing" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <span class="font-medium">No destination available.</span> The service has no email destination configured.
          </div>
        </div>
        <div *ngIf="draft.notifications.email.mode === 'enabled-custom'" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-2">
          <label class="block text-xs font-medium text-slate-700">Recipients <span class="text-red-500">*</span></label>
          <app-member-picker
            [members]="orgMembers"
            [selectedIds]="draft.notifications.email.overrideConfig?.recipientIds ?? []"
            (selectedIdsChange)="draft.notifications.email.overrideConfig = { recipientIds: $event }"
            [readOnly]="false"
          ></app-member-picker>
          <p *ngIf="emailError" class="text-xs text-red-500">At least one recipient is required.</p>
        </div>
      </div>
    </section>

    <div class="mx-5 border-t border-slate-100"></div>

    <!-- ── Ticketing Integrations ─────────────────────────────────────────────── -->
    <section class="px-5 py-5">
      <div class="mb-4 flex items-center gap-2">
        <lucide-icon name="tag" [size]="13" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
        <h3 class="text-xs font-semibold uppercase tracking-wide text-slate-500">Ticketing Integrations</h3>
      </div>

      <!-- ── GitHub Issues ──────────────────────────────────────────────────── -->
      <div class="mb-3 overflow-hidden rounded-lg border bg-white"
        [class.border-slate-200]="draft.ticketing.github.mode === 'service'"
        [class.border-violet-200]="draft.ticketing.github.mode !== 'service'"
      >
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <lucide-icon name="github" [size]="14" class="text-slate-500" [strokeWidth]="2"></lucide-icon>
            <span class="text-sm font-medium text-slate-900">GitHub Issues</span>
            <span *ngIf="draft.ticketing.github.mode !== 'service'"
              class="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Custom</span>
          </div>
          <select
            [ngModel]="draft.ticketing.github.mode"
            (ngModelChange)="setTicketMode('github', $event)"
            [ngModelOptions]="{standalone: true}"
            class="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-300"
          >
            <option value="service">Use service setting</option>
            <option value="disabled">Disabled for this entry</option>
            <option value="enabled-inherited">Enabled — use service destination</option>
            <option value="enabled-custom">Enabled — use custom destination</option>
          </select>
        </div>
        <div *ngIf="draft.ticketing.github.mode === 'service'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">{{ githubEffectiveSummary() }}</p>
        </div>
        <div *ngIf="draft.ticketing.github.mode === 'disabled'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">GitHub Issues disabled for this entry.</p>
        </div>
        <div *ngIf="draft.ticketing.github.mode === 'enabled-inherited'" class="border-t border-slate-100 px-4 py-2.5 space-y-1.5">
          <div *ngIf="!githubInheritedMissing" class="flex items-center gap-1.5 text-xs text-slate-500">
            <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
            <span>{{ githubEffectiveSummary() }}</span>
          </div>
          <div *ngIf="githubInheritedMissing" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <span class="font-medium">No destination available.</span> The service has no GitHub repository configured.
          </div>
        </div>
        <div *ngIf="draft.ticketing.github.mode === 'enabled-custom'" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-2">
          <label class="block text-xs font-medium text-slate-700">Repository <span class="text-red-500">*</span></label>
          <input
            type="text"
            [(ngModel)]="draft.ticketing.github.overrideConfig!.repository"
            [ngModelOptions]="{standalone: true}"
            placeholder="owner/repo-name"
            class="w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1"
            [class.border-slate-200]="!githubError" [class.focus:border-violet-400]="!githubError" [class.focus:ring-violet-300]="!githubError"
            [class.border-red-300]="githubError"   [class.focus:ring-red-300]="githubError"
            (ngModelChange)="draft.ticketing.github.overrideConfig = { repository: $event }"
          >
          <p *ngIf="githubError" class="text-xs text-red-500">Use the format <span class="font-mono">owner/repository</span>.</p>
        </div>
      </div>

      <!-- ── Jira ───────────────────────────────────────────────────────────── -->
      <div class="overflow-hidden rounded-lg border bg-white"
        [class.border-slate-200]="draft.ticketing.jira.mode === 'service'"
        [class.border-violet-200]="draft.ticketing.jira.mode !== 'service'"
      >
        <div class="flex items-center justify-between px-4 py-3">
          <div class="flex items-center gap-2.5">
            <div class="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-sm bg-blue-600">
              <span class="text-[9px] font-bold leading-none text-white">J</span>
            </div>
            <span class="text-sm font-medium text-slate-900">Jira</span>
            <span *ngIf="draft.ticketing.jira.mode !== 'service'"
              class="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Custom</span>
          </div>
          <select
            [ngModel]="draft.ticketing.jira.mode"
            (ngModelChange)="setTicketMode('jira', $event)"
            [ngModelOptions]="{standalone: true}"
            class="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-violet-300"
          >
            <option value="service">Use service setting</option>
            <option value="disabled">Disabled for this entry</option>
            <option value="enabled-inherited">Enabled — use service destination</option>
            <option value="enabled-custom">Enabled — use custom destination</option>
          </select>
        </div>
        <div *ngIf="draft.ticketing.jira.mode === 'service'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">{{ jiraEffectiveSummary() }}</p>
        </div>
        <div *ngIf="draft.ticketing.jira.mode === 'disabled'" class="border-t border-slate-100 px-4 py-2.5">
          <p class="text-xs text-slate-400">Jira disabled for this entry.</p>
        </div>
        <div *ngIf="draft.ticketing.jira.mode === 'enabled-inherited'" class="border-t border-slate-100 px-4 py-2.5 space-y-1.5">
          <div *ngIf="!jiraInheritedMissing" class="flex items-center gap-1.5 text-xs text-slate-500">
            <lucide-icon name="circle-check" [size]="12" class="text-emerald-500" [strokeWidth]="2"></lucide-icon>
            <span>{{ jiraEffectiveSummary() }}</span>
          </div>
          <div *ngIf="jiraInheritedMissing" class="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            <span class="font-medium">No destination available.</span> The service has no Jira project configured.
          </div>
        </div>
        <div *ngIf="draft.ticketing.jira.mode === 'enabled-custom'" class="border-t border-slate-100 px-4 pb-4 pt-3 space-y-2">
          <label class="block text-xs font-medium text-slate-700">Project Key <span class="text-red-500">*</span></label>
          <input
            type="text"
            [(ngModel)]="draft.ticketing.jira.overrideConfig!.projectKey"
            [ngModelOptions]="{standalone: true}"
            placeholder="OPS"
            class="w-full rounded-md border px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1"
            [class.border-slate-200]="!jiraError" [class.focus:border-violet-400]="!jiraError" [class.focus:ring-violet-300]="!jiraError"
            [class.border-red-300]="jiraError"   [class.focus:ring-red-300]="jiraError"
            (ngModelChange)="draft.ticketing.jira.overrideConfig = { projectKey: $event }"
          >
          <p class="text-xs text-slate-400">The Jira project key (e.g. OPS, INFRA, DATA).</p>
          <p *ngIf="jiraError" class="text-xs text-red-500">Project key is required.</p>
        </div>
      </div>
    </section>

    <div class="h-4"></div>
  </div><!-- /body -->

  <!-- ── Footer ─────────────────────────────────────────────────────────────── -->
  <div class="flex flex-shrink-0 items-center justify-end gap-3 border-t border-slate-200 bg-white px-5 py-4">
    <button
      type="button"
      (click)="requestClose()"
      [disabled]="saveState === 'saving'"
      class="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:cursor-not-allowed disabled:opacity-50"
    >Cancel</button>
    <button
      type="button"
      (click)="save()"
      [disabled]="saveState === 'saving'"
      class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-300 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
    >
      <lucide-icon *ngIf="saveState === 'saving'" name="loader" [size]="13" [strokeWidth]="2" class="animate-spin"></lucide-icon>
      {{ saveState === 'saving' ? 'Saving…' : 'Save' }}
    </button>
  </div>

</div>
